import React, { useEffect, useState } from 'react';
import { formatStay, fullDate, money, rate, when } from './format';

/**
 * Landing actuelle (/) vs Nutrition V2 (/eat), par GEO. Source : GET /api/analytics/landings (sessions `ses:<sid>`).
 * Trafic interne (cookie de Gaut, IP de Gaut, ?nonotif=1, membres connectés) exclu, visible en bas pour contrôle.
 */

const PERIODS = [['24h', '24 h'], ['7j', '7 jours'], ['30j', '30 jours'], ['tout', 'Tout']];
const GEOS = [['all', 'Tous pays'], ['US', 'États-Unis'], ['CA', 'Canada']];

function revenueLine(revenue) {
  const rows = Object.entries(revenue || {});
  return rows.length ? rows.map(([currency, cents]) => money(cents, currency)).join(' · ') : '—';
}

/** Taux de clic vers l'offre : objectif ≥ 2 %, bon 3–5 %, excellent > 5 %. */
function ctaTone(row) {
  if (row.visitors < 50) return '';
  const value = row.cta / row.visitors;
  if (value > 0.05) return 'is-great';
  if (value >= 0.03) return 'is-good';
  if (value >= 0.02) return 'is-ok';
  return 'is-low';
}

const METRICS = [
  ['Visiteurs', (row) => row.visitors],
  ['Rebond < 10 s', (row) => (row.visitors ? `${row.bounce10} · ${rate(row.bounce10, row.visitors)}` : '—')],
  ['Durée médiane', (row) => (row.medianMs === null ? '—' : formatStay(row.medianMs))],
  ['Contenu vu', (row) => (row.visitors ? `${row.contentViews} · ${rate(row.contentViews, row.visitors)}` : '—')],
  ['Prix vu', (row) => (row.visitors ? `${row.pricingViews} · ${rate(row.pricingViews, row.visitors)}` : '—')],
  ['Clic CTA hero', (row) => row.heroClicks],
  ['Clic CTA (tous)', (row) => row.cta],
  ['CTA rate', (row) => rate(row.cta, row.visitors), ctaTone],
  ['Checkout ouvert', (row) => row.checkout],
  ['Checkout rate', (row) => `${rate(row.checkout, row.visitors)}${row.cta ? ` · ${rate(row.checkout, row.cta)} des clics` : ''}`],
  ['Achats', (row) => row.purchases, (row) => (row.purchases ? 'is-paid' : '')],
  ['Conversion', (row) => rate(row.purchases, row.visitors)],
  ['CA', (row) => revenueLine(row.revenue), (row) => (row.purchases ? 'is-paid' : '')],
];

export default function Landings({ token }) {
  const [period, setPeriod] = useState('7j');
  const [geo, setGeo] = useState('all');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let stop = false;
    const load = () => fetch(`/api/analytics/landings?period=${period}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
      .then(({ ok, body }) => {
        if (stop) return;
        if (!ok) { setError(body.detail || 'Accès refusé'); return; }
        setError('');
        setData(body);
      })
      .catch(() => { if (!stop) setError('Le Worker ne répond pas.'); });
    load();
    const timer = window.setInterval(load, 15000);
    return () => { stop = true; window.clearInterval(timer); };
  }, [period, token]);

  const variants = data?.variants || [];
  return (
    <section className="ck-card ck-landings">
      <header className="ck-landings-head">
        <div>
          <h2>Landing actuelle vs Nutrition V2</h2>
          <p>
            {data?.since ? `Sessions suivies depuis le ${fullDate(data.since)}. ` : 'Collecte démarrée : en attente de la première visite. '}
            Hors trafic interne. Objectif CTA rate ≥ 2 %, bon 3–5 %, excellent &gt; 5 %.
          </p>
        </div>
        <div className="ck-landings-tools">
          <div className="ck-seg" role="tablist" aria-label="Pays">
            {GEOS.map(([id, label]) => <button key={id} type="button" className={geo === id ? 'is-on' : ''} onClick={() => setGeo(id)}>{label}</button>)}
          </div>
          <div className="ck-seg" role="tablist" aria-label="Période">
            {PERIODS.map(([id, label]) => <button key={id} type="button" className={period === id ? 'is-on' : ''} onClick={() => setPeriod(id)}>{label}</button>)}
          </div>
        </div>
      </header>
      {error && <p className="ck-error">{error}</p>}
      {!data && !error && <p className="ck-empty">Chargement…</p>}
      {data && (
        <div className="ck-table-wrap">
          <table className="ck-table ck-landings-table">
            <thead>
              <tr>
                <th />
                {variants.map((variant) => (
                  <th key={variant.id}>
                    {variant.label}
                    {variant.headlines.length > 0 && <small> · titres {variant.headlines.join(', ').toUpperCase()}</small>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map(([label, value, tone]) => (
                <tr key={label}>
                  <td>{label}</td>
                  {variants.map((variant) => {
                    const row = variant.geos[geo];
                    return <td key={variant.id} className={tone ? tone(row) : ''}>{value(row)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <details className="ck-landings-debug">
          <summary>Trafic interne exclu : {data.debug.internalSessions} session{data.debug.internalSessions > 1 ? 's' : ''} · dernières sessions</summary>
          <ul>
            {data.debug.recent.map((ses) => (
              <li key={`${ses.sid}-${ses.at}`} className={ses.internal ? 'is-internal' : ''}>
                <span>{when(ses.at)}</span>
                <span>{ses.page || (ses.variant === 'current_v70' ? '/v70' : '/')}</span>
                <span>{[ses.city, ses.country].filter(Boolean).join(', ') || '—'}</span>
                <span>{formatStay(ses.durationMs)}</span>
                <span>{ses.utm.source || 'direct'}{ses.utm.campaign ? ` · ${ses.utm.campaign}` : ''}</span>
                <span>{ses.steps.join(' → ')}</span>
                {ses.internal && <em>interne</em>}
              </li>
            ))}
          </ul>
          {!data.stripe && <p className="ck-empty">Stripe n’a pas répondu : les achats viennent seulement de la page de confirmation.</p>}
        </details>
      )}
    </section>
  );
}
